'use client';

import { useEffect } from 'react';
import { setCookie } from 'cookies-next';
import type { ReferralCampaignData } from './referralCampaign';
import {
  REFERRAL_TOKEN_COOKIE,
  REFERRER_NAME_COOKIE,
  REFERRER_USER_ID_COOKIE,
  INCENTIVE_FIELDS_COOKIE,
  LAST_CAMPAIGN_COOKIE,
  createCookieOptions,
  CookieOptions,
} from 'utils/cookies';

interface ReferralCookieSetterProps {
  campaignData: ReferralCampaignData;
  urlToken: string;
  referralType?: string;
}

/**
 * Decode base64 incentive TTL value
 */
function getIncentiveTtlValue(value?: string): number {
  if (!value) return 0;

  try {
    return parseInt(atob(value));
  } catch (e) {
    console.error('Failed to decode incentive TTL:', e);
    return 0;
  }
}

export default function ReferralCookieSetter({
  campaignData,
  urlToken,
  referralType = 'raf',
}: ReferralCookieSetterProps) {
  useEffect(() => {
    // Defer cookie setting to after page is interactive with useEffect
    const referralToken = campaignData.referral_token || urlToken;

    // Create expiration date (30 days)
    const expires = new Date();
    expires.setDate(expires.getDate() + 30);

    // Common cookie options with cross-subdomain sharing for referral cookies
    const commonOptions: CookieOptions = createCookieOptions({
      cookieOptions: { expires },
      enableCrossSubdomain: true,
    });

    const cookiesToSet: [string, string, CookieOptions][] = [];

    // Set referral_token cookie
    const referralCookieValue = JSON.stringify({
      referral_token: referralToken,
      referred_by_type: referralType,
    });
    cookiesToSet.push([REFERRAL_TOKEN_COOKIE, referralCookieValue, commonOptions]);
    cookiesToSet.push([LAST_CAMPAIGN_COOKIE, 'referral', commonOptions]);

    // Add referrer name cookie if provided
    if (campaignData.referring_user_name) {
      cookiesToSet.push([REFERRER_NAME_COOKIE, campaignData.referring_user_name, commonOptions]);
    }

    // Add referrer user ID cookie if provided
    if (campaignData.referring_user_id) {
      cookiesToSet.push([REFERRER_USER_ID_COOKIE, campaignData.referring_user_id, commonOptions]);
    }

    // Handle incentive fields
    const incentiveFields = {
      incentive_name: btoa('Urgency Offer'),
      incentive_cohort_name: campaignData.urgency_offer_incentive_cohort_name || '',
      receiver_incentive_ttl: campaignData.urgency_offer_receiver_inventive_ttl,
      referrer_user_id: campaignData.referring_user_id,
    };

    const incentiveTtl = getIncentiveTtlValue(incentiveFields.receiver_incentive_ttl) * 1000;
    const incentiveExpires = new Date(incentiveTtl);

    if (incentiveExpires > new Date()) {
      const incentiveOptions = { ...commonOptions, expires: incentiveExpires };
      cookiesToSet.push([INCENTIVE_FIELDS_COOKIE, JSON.stringify(incentiveFields), incentiveOptions]);
    }

    // Apply all cookies in batch
    cookiesToSet.forEach(([name, value, options]) => {
      setCookie(name, value, options);
    });
  }, [campaignData, urlToken, referralType]);

  return null;
}
